import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';
import { AppShell } from '@/components/layout/AppShell';
import { AdminSectionTabs } from '@/components/admin/AdminSectionTabs';
import { SubscribersSubNav } from '@/components/admin/SubscribersSubNav';
import { Button } from '@/components/ui/Button';
import { adminAutomationsService } from '@/services/admin-automations.service';
import { adminEmailTemplatesService } from '@/services/admin-email-templates.service';
import { adminSubscriberListsService } from '@/services/admin-subscriber-lists.service';
import { ApiError } from '@/services/http';
import { browserTimezone, timezoneOptions } from '@/lib/timezones';
import type { EmailTemplateView, SubscriberListView } from '@/types/api';

const inputClass =
  'h-9 rounded-lg border border-or-slate-200 bg-white px-3 text-[13px] font-normal normal-case text-or-slate-900 focus:border-or-orange-400 focus:outline-none focus:ring-2 focus:ring-or-orange-400/30';

const labelClass =
  'flex flex-col gap-1.5 text-[11px] font-semibold uppercase tracking-[0.08em] text-or-slate-500';

/**
 * Step 1 of building an automation: the shell. Rules and the audience preview
 * live on the edit page, because previewing needs a saved automation to hang
 * the send-ledger lookup off. Mirrors the broadcast create-draft-then-edit flow.
 */
export function AdminCreateAutomationPage() {
  const navigate = useNavigate();
  const [templates, setTemplates] = useState<EmailTemplateView[]>([]);
  const [lists, setLists] = useState<SubscriberListView[]>([]);
  const [name, setName] = useState('');
  const [templateId, setTemplateId] = useState('');
  const [listId, setListId] = useState('');
  const [sendHour, setSendHour] = useState(9);
  const [timezone, setTimezone] = useState(browserTimezone());
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    Promise.all([adminEmailTemplatesService.list(), adminSubscriberListsService.list()])
      .then(([t, l]) => {
        setTemplates(t);
        setLists(l);
      })
      .catch((err) => toast.error(err instanceof ApiError ? err.message : 'Could not load data.'))
      .finally(() => setLoading(false));
  }, []);

  const handleCreate = async () => {
    if (!name.trim() || !templateId || !listId || creating) return;
    setCreating(true);
    try {
      const created = await adminAutomationsService.create({
        name: name.trim(),
        listId,
        templateId,
        sendHour,
        timezone,
      });
      navigate(`/admin/subscribers/automations/${created.id}`);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Could not create automation.');
      setCreating(false);
    }
  };

  return (
    <AppShell mode="parent">
      <div className="flex flex-col gap-6">
        <AdminSectionTabs />
        <SubscribersSubNav />

        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => navigate('/admin/subscribers/automations')}
            className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-or-slate-200 bg-white text-or-slate-600 hover:bg-or-slate-50"
            aria-label="Back to automations"
          >
            <ArrowLeft className="h-4 w-4" aria-hidden />
          </button>
          <div>
            <div className="or-kicker text-or-orange-600">New automation</div>
            <h1 className="mt-0.5 font-display text-[22px] font-bold text-or-slate-900 sm:text-[26px]">
              Create an automation
            </h1>
          </div>
        </div>

        {loading ? (
          <div className="py-12 text-center text-or-slate-500">Loading…</div>
        ) : (
          <div className="flex max-w-lg flex-col gap-5 rounded-2xl border border-or-slate-200 bg-white p-6">
            <label className={labelClass}>
              Name
              <input
                value={name}
                onChange={(e) => setName(e.target.value)}
                maxLength={120}
                placeholder="Day 3 nudge"
                className={inputClass}
              />
            </label>

            <label className={labelClass}>
              Template
              {templates.length === 0 ? (
                <p className="text-[12px] font-normal normal-case text-or-slate-500">
                  No templates yet —{' '}
                  <Link
                    to="/admin/subscribers/templates/new"
                    className="text-or-orange-600 underline"
                  >
                    create one first
                  </Link>
                  .
                </p>
              ) : (
                <select
                  value={templateId}
                  onChange={(e) => setTemplateId(e.target.value)}
                  className={inputClass}
                >
                  <option value="">Choose a template…</option>
                  {templates.map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.name} ({t.format})
                    </option>
                  ))}
                </select>
              )}
            </label>

            <label className={labelClass}>
              List
              <select
                value={listId}
                onChange={(e) => setListId(e.target.value)}
                className={inputClass}
              >
                <option value="">Choose a list…</option>
                {lists.map((l) => (
                  <option key={l.id} value={l.id}>
                    {l.name} ({l.memberCount} members)
                  </option>
                ))}
              </select>
            </label>

            <div className="flex gap-3">
              <label className={`${labelClass} w-28`}>
                Send at
                <select
                  value={sendHour}
                  onChange={(e) => setSendHour(Number(e.target.value))}
                  className={inputClass}
                >
                  {Array.from({ length: 24 }, (_, h) => (
                    <option key={h} value={h}>
                      {h % 12 === 0 ? 12 : h % 12}
                      {h < 12 ? 'am' : 'pm'}
                    </option>
                  ))}
                </select>
              </label>

              <label className={`${labelClass} flex-1`}>
                Timezone
                <select
                  value={timezone}
                  onChange={(e) => setTimezone(e.target.value)}
                  className={inputClass}
                >
                  {timezoneOptions().map((tz) => (
                    <option key={tz} value={tz}>
                      {tz}
                    </option>
                  ))}
                </select>
              </label>
            </div>

            <p className="text-[12px] text-or-slate-500">
              You'll add the rules and preview the audience next. Nothing sends until you turn the
              automation on.
            </p>

            <div className="flex justify-end">
              <Button
                variant="primary"
                size="md"
                onClick={handleCreate}
                disabled={!name.trim() || !templateId || !listId || creating}
              >
                {creating ? 'Creating…' : 'Continue'}
              </Button>
            </div>
          </div>
        )}
      </div>
    </AppShell>
  );
}
